import * as React from 'react';

import {
    Box,
    Card,
    CircularProgress,
    Divider,
    IconButton,
    Pagination,
    Tooltip,
    Typography,
} from '@mui/material';
import { faEdit } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
    BoxSkeleton,
    footerHeight,
    NoDataCard,
    Scrollbar,
    SkeletonLoader,
    TopbarHeight,
    useModal,
} from '@europrocurement/flexy-components';
import { ReminderPublisher } from '@europrocurement/l2d-domain';
import { UseKeycloakCheckRole } from '@europrocurement/l2d-keycloak';
import { contactSupport, retryLater } from '@europrocurement/l2d-utils';

import CommentEditionModal from './CommentEditionModal';
import { MailContent } from '../hooks/useMailContent';

/**
 * Props pour le composant MailPreview
 */
type MailPreviewProps = {
    /** Contenu du mail à afficher */
    mailContent: MailContent | null;
    /** Indique si le contenu du mail est en cours de chargement */
    isLoading: boolean;
    /** Page courante des pièces jointes */
    attachmentPage?: number;
    /** Nombre total de pièces jointes */
    attachmentCount?: number;
    /** Gestionnaire pour le changement de page des pièces jointes */
    onAttachmentPageChange?: (page: number) => void;
    /** Informations du rappel fournisseur */
    reminderPublisher?: ReminderPublisher;
};

/**
 * Composant d'affichage de la prévisualisation d'un mail
 *
 * Affiche le sujet, le destinataire et le corps du message
 * Inclut une pagination pour les pièces jointes si fournies
 */
const MailPreview: React.FunctionComponent<MailPreviewProps> = function ({
    mailContent,
    isLoading,
    attachmentPage = 1,
    attachmentCount = 0,
    onAttachmentPageChange,
    reminderPublisher,
}) {
    const { modalActions } = useModal();
    const roleChecker = UseKeycloakCheckRole();
    const isTech = roleChecker('realm:debug');

    /**
     * Affiche la modal d'édition du commentaire
     */
    const displayModal = React.useCallback(() => {
        modalActions.call(
            <CommentEditionModal
                reminderPublisher={reminderPublisher}
                onClose={() => {
                    modalActions.close();
                }}
            />,
            true,
        );
    }, [modalActions, reminderPublisher]);

    /**
     * Gestion du changement de pagination
     */
    const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
        if (onAttachmentPageChange) {
            onAttachmentPageChange(page);
        }
    };

    /**
     * Rendu du contenu approprié en fonction de l'état de chargement et de la disponibilité des données
     */
    const renderContent = () => {
        if (isLoading) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <CircularProgress />
                </Box>
            );
        }

        const noContentComponent = (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%',
                    paddingTop: '15vh',
                    paddingBottom: '15vh',
                }}
            >
                <NoDataCard
                    messageOverwrite={
                        <>
                            <Typography variant="h3">Contenu du mail indisponible</Typography>
                            <Typography variant="subtitle2">
                                {`
                                    ${retryLater} 
                                    ${!isTech ? contactSupport : ''}
                                `}
                            </Typography>
                        </>
                    }
                />
            </Box>
        );

        return (
            <Box sx={{ padding: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        gap: 3,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                            width: '40%',
                        }}
                    >
                        <Box sx={{ marginBottom: 2 }}>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                            >
                                Destinataire:
                            </Typography>
                            <Typography variant="body1">
                                {mailContent?.recipient?.email || ' - '}
                            </Typography>
                        </Box>
                        <Box sx={{ marginBottom: 2 }}>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                            >
                                Sujet:
                            </Typography>
                            <Typography variant="body1">{mailContent?.subject || ' - '}</Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '60%',
                            paddingRight: '10px',
                        }}
                    >
                        <Box sx={{ marginBottom: 2 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'baseline',
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    Commentaire complémentaire:
                                </Typography>
                                <Tooltip title="Modifier le message complémentaire">
                                    <IconButton
                                        onClick={displayModal}
                                        size="small"
                                        color="secondary"
                                        disabled={reminderPublisher?.status === 'sent'}
                                    >
                                        <FontAwesomeIcon icon={faEdit} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box>
                                <SkeletonLoader
                                    isLoading={!reminderPublisher}
                                    skeleton={
                                        <BoxSkeleton sx={{ width: '100%', height: '100px' }} />
                                    }
                                >
                                    <Typography
                                        sx={{
                                            wordBreak: 'break-word',
                                            whiteSpace: 'pre-wrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                        }}
                                    >
                                        {reminderPublisher?.customMessage}
                                    </Typography>
                                </SkeletonLoader>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <Divider sx={{ marginY: 2 }} />
                {mailContent ? (
                    <Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'baseline',
                                gap: 1,
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ marginBottom: 1 }}
                            >
                                Corps du message:
                            </Typography>
                        </Box>
                        <div dangerouslySetInnerHTML={{ __html: mailContent.body }} />
                    </Box>
                ) : (
                    noContentComponent
                )}
            </Box>
        );
    };

    const renderToolbox = () => (
        <Box
            sx={{
                paddingRight: '5%',
                paddingLeft: '5%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
            }}
        />
    );

    /**
     * Rendu de la section de pagination s'il y a des pièces jointes
     */
    const renderPagination = () => {
        if (attachmentCount <= 0) {
            return null;
        }

        return (
            <Box
                sx={{
                    padding: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <Pagination
                    count={attachmentCount}
                    page={attachmentPage}
                    onChange={handlePageChange}
                />
            </Box>
        );
    };

    return (
        <Card
            sx={{
                height: `calc(100vh - ${footerHeight}px - ${TopbarHeight}px - 45px - 95px)`,
                display: 'flex',
                flexDirection: 'column',
                marginTop: 0,
            }}
        >
            {renderToolbox()}
            <Scrollbar>
                <Box
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        backgroundColor: 'background.paper',
                    }}
                >
                    {renderContent()}
                </Box>
            </Scrollbar>
            {renderPagination()}
        </Card>
    );
};

export default MailPreview;
